import { MainApi } from 'api/MainApi'
import { MissionJobRead, OdysseyRead } from 'api/schema'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PATH } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { IMissionInfo } from 'utils/interfaces'

import style from './SharedMissionItem.module.css'
import notCalculatedStyle from 'styles/missionRowNotCalculated.module.css'

export interface ISharedMissionItemProps {
  mission: IMissionInfo
  tasks: MissionJobRead[]
  odysseys: OdysseyRead[]
  onMissionsDeleted: (deletedId?: number) => void
  onClick?: () => void
  highlighted?: boolean
  showCheckbox?: boolean
  checked?: boolean
  onCheckboxChange?: (checked: boolean) => void
  onAddtoScenario?: (id: number) => void
  onLaunch?: (mission: IMissionInfo) => void
}

const SharedMissionItem = ({
  mission,
  tasks,
  odysseys,
  onMissionsDeleted,
  onClick,
  highlighted,
  showCheckbox,
  checked,
  onCheckboxChange,
  onAddtoScenario,
  onLaunch,
}: ISharedMissionItemProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [deleteMissionDialog, setDeleteMissionDialog] = useState(false)

  const goToMission = () => {
    navigate(`${PATH.MISSION}${mission.id}`)
  }

  const deleteMission = async () => {
    try {
      await MainApi.deleteMission(mission.id)
      setDeleteMissionDialog(false)
      onMissionsDeleted(mission.id)
    } catch (error) {
      console.log('Error deleting mission:', error)
    }
  }

  const copyMission = async () => {
    try {
      await MainApi.copyMission(mission.id)
      onMissionsDeleted()
    } catch (error) {
      console.log('Error copying mission:', error)
    }
  }

  const AddtoScenario = () => {
    if (onAddtoScenario) {
      onAddtoScenario(mission.id)
    }
  }

  const tasksUsingMission = tasks.filter(
    (task: MissionJobRead) => task.mission_id === mission.id && task.job_type !== 255 // MissionJobTypeEnum.DELETE_SCENARIO
  )
  const odysseysUsingMission = odysseys.filter((odyssey: OdysseyRead) => odyssey.missions.includes(mission.id))
  const usingMission = [...tasksUsingMission, ...odysseysUsingMission]
  const isMissionActive = usingMission.length > 0

  return (
    <>
      <div
        className={clsx(style.missionRow, {
          [style.highlighted]: highlighted,
          [notCalculatedStyle.missionRowNotCalculated]: mission.status !== 'ok',
        })}
        onClick={onClick ? onClick : goToMission}
      >
        <div></div>
        <span className={style.colLeft}>{mission.id}</span>
        <div className={style.colLeftMultiline} title={mission.name}>
          {mission.name}
        </div>
        <div className={style.colCenterMultiline}>
          {usingMission.length > 0 ? (
            usingMission.map((item: MissionJobRead | OdysseyRead) => item.id).join(', ')
          ) : (
            <span>{t('No tasks')}</span>
          )}
        </div>
        <span className={style.colCenter} onClick={showCheckbox ? e => e.stopPropagation() : undefined}>
          {showCheckbox ? (
            <input type="checkbox" checked={checked} onChange={e => onCheckboxChange?.(e.target.checked)} />
          ) : mission.location === null || mission.location === undefined ? (
            'GNSS'
          ) : (
            'SLAM'
          )}
        </span>
        <span className={style.colCenter} onClick={e => e.stopPropagation()}>
          <ActionsMenu>
            <ActionsMenuItem
              onClick={() => onLaunch?.(mission)}
              data-test-id={getTestId('launch-mission')}
              disabled={mission.status !== 'ok'}
            >
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Launch</span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={() => navigate(`${PATH.DASHBOARD}?missionId=${mission.id}`)}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Dashboard</span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={() => goToMission()} data-test-id={getTestId('edit-zone-list')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText} data-test-id={getTestId('edit-title')}>
                {t('common.edit')}
              </span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={() => copyMission()} data-test-id={getTestId('copy-zone-list')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText} data-test-id={getTestId('copy-title')}>
                Copy
              </span>
            </ActionsMenuItem>
            {onAddtoScenario && (
              <ActionsMenuItem onClick={() => AddtoScenario()} data-test-id={getTestId('add-to-scenario-zone-list')}>
                <span className={style.menuItemIcon} />
                <span className={style.menuItemText} data-test-id={getTestId('add-to-scenario-title')}>
                  Add to an Scenario
                </span>
              </ActionsMenuItem>
            )}
            <ActionsMenuItem
              onClick={() => setDeleteMissionDialog(true)}
              data-test-id={getTestId('remove-zone-list')}
              disabled={isMissionActive}
            >
              <RemoveSvg className={style.menuItemIcon} />
              <span data-test-id={getTestId('delete-title')}>{t('common.delete')}</span>
            </ActionsMenuItem>
          </ActionsMenu>
        </span>
      </div>
      <ConfirmDialog
        title={t('missions.delete_question')}
        isOpen={deleteMissionDialog}
        onClose={() => setDeleteMissionDialog(false)}
        onConfirmClick={deleteMission}
        confirmText={t('common.delete')}
        danger={true}
      />
    </>
  )
}

export default SharedMissionItem
